import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import Map, { Source, Layer, NavigationControl, Popup } from 'react-map-gl/maplibre';
import type { MapRef, ViewStateChangeEvent, MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { GeoJSONFeatureCollection, MapBounds, ParcelDetail, GeoJSONFeature } from '../types/filters';
import 'maplibre-gl/dist/maplibre-gl.css';

// Map center: approximately center of zachodniopomorskie/lubuskie
const INITIAL_VIEW_STATE = {
  longitude: 15.5,
  latitude: 52.5,
  zoom: 7,
};

// Map style (free Carto basemap)
const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

// Tender type labels (Polish)
const TENDER_TYPE_LABELS: Record<string, string> = {
  sale: 'Sprzedaż',
  lease: 'Dzierżawa',
  auction: 'Przetarg',
  other: 'Inne',
};

// Colors for active tenders (by type)
const ACTIVE_COLORS: Record<string, string> = {
  sale: '#2563eb',    // Blue
  lease: '#16a34a',   // Green
  auction: '#dc2626', // Red
  other: '#6b7280',   // Gray
  default: '#2563eb',
};

// Colors for expired tenders (muted versions)
const EXPIRED_COLORS: Record<string, string> = {
  sale: '#93c5fd',    // Light blue
  lease: '#86efac',   // Light green
  auction: '#fca5a5', // Light red
  other: '#d1d5db',   // Light gray
  default: '#93c5fd',
};

// Legend items for display
const LEGEND_ITEMS = ['sale', 'lease', 'auction', 'other'] as const;

// Get current date as ISO string (YYYY-MM-DD) for comparison
function getTodayDateString(): string {
  return new Date().toISOString().split('T')[0];
}

// Layer styles - using any to avoid complex maplibre type issues
function getCircleLayerStyle(): Record<string, unknown> {
  const today = getTodayDateString();

  return {
    id: 'parcels-circle',
    type: 'circle',
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        7, 4,
        10, 6,
        14, 10,
      ],
      'circle-color': [
        'case',
        // Check if tender_date is in the past (before today)
        ['<', ['get', 'tender_date'], today],
        // Expired tenders: use muted colors based on type
        [
          'match',
          ['get', 'tender_type'],
          'sale', EXPIRED_COLORS.sale,
          'lease', EXPIRED_COLORS.lease,
          'auction', EXPIRED_COLORS.auction,
          'other', EXPIRED_COLORS.other,
          EXPIRED_COLORS.default,
        ],
        // Active tenders: use normal colors based on type
        [
          'match',
          ['get', 'tender_type'],
          'sale', ACTIVE_COLORS.sale,
          'lease', ACTIVE_COLORS.lease,
          'auction', ACTIVE_COLORS.auction,
          'other', ACTIVE_COLORS.other,
          ACTIVE_COLORS.default,
        ],
      ],
      'circle-stroke-width': 1,
      'circle-stroke-color': '#ffffff',
    },
  };
}

interface FlyToTarget {
  lat: number;
  lng: number;
  zoom?: number;
}

interface MapComponentProps {
  parcels: GeoJSONFeatureCollection | null;
  selectedParcel: ParcelDetail | null;
  loading: boolean;
  error: string | null;
  flyTo?: FlyToTarget | null;
  onBoundsChange: (bounds: MapBounds) => void;
  onParcelClick: (id: string | null) => void;
}

export function MapComponent({
  parcels,
  selectedParcel,
  loading,
  error,
  flyTo,
  onBoundsChange,
  onParcelClick,
}: MapComponentProps) {
  const mapRef = useRef<MapRef>(null);
  const [popupInfo, setPopupInfo] = useState<GeoJSONFeature['properties'] & { lng: number; lat: number } | null>(null);

  // Memoize circle layer style (recalculates daily for date comparison)
  const circleLayerStyle = useMemo(() => getCircleLayerStyle(), []);

  // Get bounds when map moves
  const handleMoveEnd = useCallback(
    (e: ViewStateChangeEvent) => {
      const map = mapRef.current;
      if (!map) return;

      const bounds = map.getBounds();
      if (!bounds) return;

      onBoundsChange({
        min_lat: bounds.getSouth(),
        max_lat: bounds.getNorth(),
        min_lng: bounds.getWest(),
        max_lng: bounds.getEast(),
      });
    },
    [onBoundsChange]
  );

  // Load parcels on initial load
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      const bounds = map.getBounds();
      if (bounds) {
        onBoundsChange({
          min_lat: bounds.getSouth(),
          max_lat: bounds.getNorth(),
          min_lng: bounds.getWest(),
          max_lng: bounds.getEast(),
        });
      }
    }
  }, [onBoundsChange]);

  // Fly to location when flyTo prop changes
  useEffect(() => {
    if (flyTo && mapRef.current) {
      mapRef.current.flyTo({
        center: [flyTo.lng, flyTo.lat],
        zoom: flyTo.zoom ?? 12,
        duration: 1500,
      });
    }
  }, [flyTo]);

  // Handle click on parcel
  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      if (feature && feature.properties) {
        const geometry = feature.geometry as GeoJSON.Point;
        setPopupInfo({
          ...feature.properties as GeoJSONFeature['properties'],
          lng: geometry.coordinates[0],
          lat: geometry.coordinates[1],
        });
        onParcelClick(feature.properties.id as string);
      } else {
        setPopupInfo(null);
        onParcelClick(null);
      }
    },
    [onParcelClick]
  );

  // Cursor style on hover
  const handleMouseEnter = useCallback(() => {
    const map = mapRef.current;
    if (map) {
      map.getCanvas().style.cursor = 'pointer';
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current;
    if (map) {
      map.getCanvas().style.cursor = '';
    }
  }, []);

  // Empty state
  const isEmpty = parcels && parcels.features.length === 0;

  return (
    <div className="map-container">
      {loading && (
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <span>Ładowanie działek...</span>
        </div>
      )}

      {error && (
        <div className="map-error">
          <span>Błąd: {error}</span>
        </div>
      )}

      {isEmpty && !loading && (
        <div className="map-empty">
          <span>Brak działek spełniających kryteria</span>
        </div>
      )}

      <Map
        ref={mapRef}
        initialViewState={INITIAL_VIEW_STATE}
        style={{ width: '100%', height: '100%' }}
        mapStyle={MAP_STYLE}
        onMoveEnd={handleMoveEnd}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        interactiveLayerIds={['parcels-circle']}
      >
        <NavigationControl position="top-right" />

        {parcels && (
          <Source id="parcels" type="geojson" data={parcels}>
            {/* @ts-expect-error - maplibre layer style types are complex */}
            <Layer {...circleLayerStyle} />
          </Source>
        )}

        {popupInfo && (
          <Popup
            longitude={popupInfo.lng}
            latitude={popupInfo.lat}
            anchor="bottom"
            onClose={() => {
              setPopupInfo(null);
              onParcelClick(null);
            }}
            closeButton={true}
            closeOnClick={false}
          >
            <div className="popup-content">
              <h4>Działka {popupInfo.parcel_number}</h4>
              {popupInfo.price && (
                <p>Cena: {popupInfo.price.toLocaleString('pl-PL')} zł</p>
              )}
              {popupInfo.tender_date && (
                <p>Data: {new Date(popupInfo.tender_date).toLocaleDateString('pl-PL')}</p>
              )}
              {popupInfo.tender_type && (
                <p className={`tender-type tender-type-${popupInfo.tender_type}`}>
                  {popupInfo.tender_type === 'sale' && 'Sprzedaż'}
                  {popupInfo.tender_type === 'lease' && 'Dzierżawa'}
                  {popupInfo.tender_type === 'auction' && 'Przetarg'}
                  {popupInfo.tender_type === 'other' && 'Inne'}
                </p>
              )}
            </div>
          </Popup>
        )}
      </Map>

      {/* Map Legend */}
      <div className="map-legend">
        <div className="map-legend-section">
          <div className="map-legend-title">Aktywne</div>
          {LEGEND_ITEMS.map((type) => (
            <div key={`active-${type}`} className="map-legend-item">
              <span
                className="map-legend-dot"
                style={{ backgroundColor: ACTIVE_COLORS[type] }}
              />
              <span className="map-legend-label">{TENDER_TYPE_LABELS[type]}</span>
            </div>
          ))}
        </div>
        <div className="map-legend-section">
          <div className="map-legend-title">Nieaktualne</div>
          {LEGEND_ITEMS.map((type) => (
            <div key={`expired-${type}`} className="map-legend-item">
              <span
                className="map-legend-dot"
                style={{ backgroundColor: EXPIRED_COLORS[type] }}
              />
              <span className="map-legend-label">{TENDER_TYPE_LABELS[type]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
